import {
    useCallback,
    useState,
} from 'react'

import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import {
    ContractBasis,
} from 'app/Apps/Pricing/Pricing.types'
import {
    useArchive,
    ArchiveButton,
    ArchiveConfirmationDialog,
} from 'app/shared-components/Archive'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'

import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import requests from 'app/requests/ContractBasis/ContractBasis.requests'
import useHasPermission from 'app/hooks/useHasPermission'

import contractBasisFields from './contractBasisDetails.fields'
import useStyles from './ContractBasisDetails.styles'

type Props = {
    onSuccess: (newContractBasis: any) => void,
    data?: ContractBasis,
}

export default function ContractBasisDetails({
    onSuccess,
    data,
}: Readonly<Props>) {
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const contractBasisUpdatePermission = useHasPermission(['contract_basis_update'])

    const {
        ContractBasis: ContractBasisRoutes,
    } = usePricingRoutes()
    const [
        hasNDA,
        setHasNDA,
    ] = useState(data?.hasNDA)

    const exists = Boolean(data?.id)
    const fieldsWithClasses = useFieldsWithClassName(
        contractBasisFields(hasNDA, getAllowedValues, exists),
        classes,
    )

    const archive = useArchive({
        requestParamFn: data?.archived ? requests.restore
            : requests.archive,
        notificationId: 'ContractBasisUpdateDto',
        title: 'Contract Basis',
        data,
        redirectionAfterSuccess: generateSkyNetLink({
            domainPath: ContractBasisRoutes.path,
        }),
    })

    const setExternalValue = useCallback((update: ContractBasis) => {
        setHasNDA(update.hasNDA)
    }, [])

    const modifyDataBeforeSend = useCallback((newData) => {
        if (newData.hasNDA) {
            return {
                ...newData,
                account: undefined,
            }
        }
        return newData
    }, [])

    return (
        <>
            <FormWithControls
                disabled={!contractBasisUpdatePermission}
                name={requests.domainName}
                setExternalValue={setExternalValue}
                value={data}
                fields={fieldsWithClasses}
                classNames={{
                    gridWrapper: classes.gridWrapper,
                }}
                onSuccess={onSuccess}
                modifyDataBeforeSend={modifyDataBeforeSend}
                requestParams={exists ? requests.update({
                    id: data?.id,
                }) : requests.create()}
                customButtons={exists ? [<ArchiveButton
                    onClick={archive.handleArchiveRestore}
                    archived={data?.archived}
                />] : undefined}
                exists={exists}
            />
            <ArchiveConfirmationDialog
                title="Contract Basis"
                openConfirmDialog={archive.openConfirmDialog}
                handleClose={archive.handleArchiveRestoreConfirm}
                archived={data?.archived}
            />
        </>
    )
}
